import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import eap3dModel from "@/assets/eap-3d-model.png";
import { useLanguage } from "@/contexts/LanguageContext";

export const Hero = () => {
  const { language, setLanguage } = useLanguage();
  
  const translations = {
    de: {
      badge: "Revolutioniert die Implantatprothetik",
      headline: "Mehr als Abutments",
      description: "Die neue Generation von Abutments! Patientenindividuell. Schnell & sicher.",
      smarter: "SMARTER.",
      registerButton: "Jetzt kostenlos registrieren",
      consultButton: "Beratung anfragen",
      valueProp: "✓ Kostenlose Registrierung   ✓ Sofort einsatzbereit   ✓ Keine Mindestbestellmenge",
      countries: "Länder patentiert",
      original: "Das Original",
      since: "Start der wissenschaftlichen Forschung",
      patented: "Weltweit patentiert",
      quality: "Höchste Qualität"
    },
    en: {
      badge: "Revolutionizing implant prosthetics",
      headline: "More than Abutments",
      description: "The new generation of abutments! Patient-specific. Fast & safe.",
      smarter: "SMARTER.",
      registerButton: "Register for free now",
      consultButton: "Request consultation",
      valueProp: "✓ Free registration   ✓ Ready to use immediately   ✓ No minimum order quantity",
      countries: "Countries patented",
      original: "The Original",
      since: "Since",
      patented: "Patented worldwide",
      quality: "Highest quality"
    },
    fr: {
      badge: "Révolutionne la prothèse implantaire",
      headline: "Plus que des piliers",
      description: "La nouvelle génération de piliers ! Spécifique au patient. Rapide et sûr.",
      smarter: "PLUS INTELLIGENT.",
      registerButton: "S'inscrire gratuitement maintenant",
      consultButton: "Demander une consultation",
      valueProp: "✓ Inscription gratuite   ✓ Prêt à l'emploi immédiatement   ✓ Pas de quantité minimale de commande",
      countries: "Pays brevetés",
      original: "L'original",
      since: "Début",
      patented: "Breveté dans le monde entier",
      quality: "Qualité supérieure"
    },
    nl: {
      badge: "Revolutioneert implantaatprothetiek",
      headline: "Meer dan abutments",
      description: "De nieuwe generatie abutments! Patiëntspecifiek. Snel & veilig.",
      smarter: "SLIMMER.",
      registerButton: "Nu gratis registreren",
      consultButton: "Advies aanvragen",
      valueProp: "✓ Gratis registratie   ✓ Direct klaar voor gebruik   ✓ Geen minimale bestelhoeveelheid",
      countries: "Landen gepatenteerd",
      original: "Het origineel",
      since: "Begin",
      patented: "Wereldwijd gepatenteerd",
      quality: "Hoogste kwaliteit"
    },
    it: {
      badge: "Rivoluziona la protesi implantare",
      headline: "Più di abutment",
      description: "La nuova generazione di abutment! Specifico per il paziente. Veloce e sicuro.",
      smarter: "PIÙ INTELLIGENTE.",
      registerButton: "Registrati gratuitamente ora",
      consultButton: "Richiedi consulenza",
      valueProp: "✓ Registrazione gratuita   ✓ Pronto all'uso immediatamente   ✓ Nessuna quantità minima d'ordine",
      countries: "Paesi brevettati",
      original: "L'originale",
      since: "Inizio",
      patented: "Brevettato in tutto il mondo",
      quality: "Massima qualità"
    },
    es: {
      badge: "Revoluciona la prótesis de implantes",
      headline: "Más que pilares",
      description: "¡La nueva generación de pilares! Específico para el paciente. Rápido y seguro.",
      smarter: "MÁS INTELIGENTE.",
      registerButton: "Regístrese gratis ahora",
      consultButton: "Solicitar consulta",
      valueProp: "✓ Registro gratuito   ✓ Listo para usar inmediatamente   ✓ Sin cantidad mínima de pedido",
      countries: "Países patentados",
      original: "El original",
      since: "Inicio",
      patented: "Patentado en todo el mundo",
      quality: "La más alta calidad"
    }
  };
  
  const t = translations[language];
  
  const videoSrc = language === "de" 
    ? "/videos/eap-trailer-de.mp4" 
    : "/videos/eap-trailer-en.mp4";

  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-hero -z-10" />
      
      <div className="container mx-auto px-4 py-12">

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <div className="space-y-8 animate-fade-up">
            <div className="inline-block">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
                {t.badge}
              </span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-foreground leading-tight italic">
              {t.headline}
            </h1>
            
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-xl">
              {t.description} <span className="font-bold">{t.smarter}</span>
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-primary hover:bg-primary-hover text-primary-foreground shadow-glow group text-base sm:text-lg px-8 py-6"
                onClick={() => window.location.href = 'https://shop.eap-abutments.com/login/signup'}
              >
                {t.registerButton}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-primary/20 text-foreground hover:bg-primary/10 hover:border-primary text-base sm:text-lg px-8 py-6"
                onClick={() => window.location.href = '#kontakt'}
              >
                {t.consultButton}
              </Button>
            </div>

            {/* Value Proposition */}
            <div className="bg-card/50 backdrop-blur-sm p-4 rounded-xl border border-primary/20">
              <ul className="text-sm text-muted-foreground space-y-1.5 sm:space-y-0 sm:flex sm:flex-wrap sm:gap-x-6 sm:gap-y-1.5">
                {t.valueProp
                  .split("   ")
                  .map((item) => item.replace(/^✓\s*/, "").trim())
                  .filter(Boolean)
                  .map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="text-primary font-bold" aria-hidden="true">✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
              </ul>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap gap-8 pt-8 border-t border-border">
              <div>
                <div className="text-3xl font-bold text-primary">45+</div>
                <div className="text-sm text-muted-foreground">{t.countries}</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-primary">EAP®</div>
                <div className="text-sm text-muted-foreground">{t.original}</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-primary">2009</div>
                <div className="text-sm text-muted-foreground">{t.since}</div>
              </div>
            </div>
          </div>

          {/* Right Column - Video */}
          <div className="relative animate-fade-in">
            <div className="relative rounded-2xl overflow-hidden shadow-card bg-gradient-to-br from-secondary/20 to-background">
              <video 
                key={videoSrc}
                src={videoSrc}
                controls
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-auto"
                preload="none"
              >
                Ihr Browser unterstützt keine Videos.
              </video>
            </div>
            
            {/* Floating Card */}
            <div className="absolute -bottom-20 right-0 bg-card p-6 rounded-xl shadow-glow border border-border hidden lg:block animate-scale-in">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <div className="font-semibold text-foreground">{t.patented}</div>
                  <div className="text-sm text-muted-foreground">{t.quality}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
